import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Upload, FileText, CheckCircle2, AlertTriangle, Download } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { Revision, ResultadoImportacion } from '@/api/tienda.api';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import { dinero } from '@/lib/dinero';

/**
 * IMPORTAR INVENTARIO — dos pasos, y el del medio es el que importa.
 *
 *   1. Se elige el archivo y se REVISA sin guardar nada.
 *   2. Recién con el resumen a la vista se confirma.
 *
 * Un archivo de inventario real viene con códigos repetidos, precios en blanco
 * y tallas mal escritas. Descubrirlo después de haber escrito 300 filas obliga
 * a limpiar a mano una base ya sucia. Descubrirlo antes cuesta un vistazo.
 */

export default function ImportarInventario() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [texto, setTexto] = useState('');
  const [motivo, setMotivo] = useState('');
  const [revision, setRevision] = useState<Revision | null>(null);
  const [resultado, setResultado] = useState<ResultadoImportacion | null>(null);

  const revisar = useMutation({
    mutationFn: () => tienda.revisarImportacion(texto),
    onSuccess: (r) => setRevision(r),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo leer el archivo'),
  });

  const importar = useMutation({
    mutationFn: () => tienda.importarInventario(texto, motivo.trim() || undefined),
    onSuccess: (r) => {
      setResultado(r);
      void qc.invalidateQueries({ queryKey: ['productos'] });
      void qc.invalidateQueries({ queryKey: ['inventario'] });
      toast.success('Inventario cargado');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo importar'),
  });

  async function tomarArchivo(f: File | undefined) {
    if (!f) return;
    const contenido = await f.text();
    setTexto(contenido);
    setRevision(null);
    setResultado(null);
  }

  // ── Paso 3: ya se importó ────────────────────────────────────────────────
  if (resultado) {
    return (
      <div className="space-y-6">
        <PageHeader title="Inventario cargado" />
        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">Listo</p>
            </div>
            <dl className="grid gap-2 sm:grid-cols-2">
              <Dato etiqueta="Productos nuevos" valor={resultado.productosCreados} />
              <Dato etiqueta="Productos actualizados" valor={resultado.productosActualizados} />
              <Dato etiqueta="Tallas nuevas" valor={resultado.variantesCreadas} />
              <Dato etiqueta="Piezas que entraron" valor={resultado.piezasIngresadas} />
            </dl>

            {resultado.problemas.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">
                  {resultado.problemas.length} pieza(s) no se pudieron cargar:
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
                  {resultado.problemas.map((p, i) => (
                    <li key={i}>
                      <span className="font-mono">{p.codigo}</span> — {p.motivo}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-amber-800">
                  El resto sí entró. Corregí esas filas y vuelve a subir solo esas.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={() => navigate('/productos')}>Ver el catálogo</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setTexto('');
                  setRevision(null);
                  setResultado(null);
                }}
              >
                Cargar otro archivo
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/productos')}>
          <ArrowLeft className="h-4 w-4" />
          Productos
        </Button>
      </div>

      <PageHeader
        title="Cargar inventario de un golpe"
        description="Primero te muestro qué va a pasar. No se guarda nada hasta que confirmes."
      />

      {/* ── Paso 1: el archivo ────────────────────────────────────────────── */}
      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-surface-border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Upload className="h-4 w-4" />
              Elegir archivo
              <input
                type="file"
                accept=".csv,.txt,.tsv"
                className="hidden"
                onChange={(e) => void tomarArchivo(e.target.files?.[0])}
              />
            </label>
            <Button variant="ghost" size="sm" onClick={() => void descargarPlantilla()}>
              <Download className="h-4 w-4" />
              Bajar la plantilla
            </Button>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              …o pegalo acá directo
            </label>
            <Textarea
              rows={8}
              className="font-mono text-xs"
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setRevision(null);
              }}
              placeholder={'codigo,nombre,categoria,precio,costo,talla,cantidad,aviso,alias,lugar\n005,Vestido rosado,Vestidos,1800,950,S,3,2,el rosado,Tienda Santiago'}
            />
            <p className="mt-1 text-xs text-slate-500">
              Una fila por talla. Un vestido con tres tallas son tres filas con el mismo código.
              La última columna, «lugar», es opcional: vacía entra al almacén; con el nombre de una
              tienda, entra a esa tienda. Sirve copiar y pegar desde Excel.
            </p>
          </div>

          <Button onClick={() => revisar.mutate()} disabled={!texto.trim() || revisar.isPending}>
            <FileText className="h-4 w-4" />
            {revisar.isPending ? 'Leyendo…' : 'Revisar sin guardar'}
          </Button>
        </CardBody>
      </Card>

      {/* ── Paso 2: qué va a pasar ────────────────────────────────────────── */}
      {revision && (
        <Card>
          <CardBody className="space-y-4">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">Esto es lo que va a pasar</h3>

            <dl className="grid gap-2 sm:grid-cols-3">
              <Dato etiqueta="Filas leídas" valor={revision.resumen.filasLeidas} />
              <Dato etiqueta="Productos nuevos" valor={revision.resumen.productosNuevos} />
              <Dato etiqueta="Ya estaban" valor={revision.resumen.productosQueYaEstaban} />
              <Dato etiqueta="Tallas nuevas" valor={revision.resumen.variantesNuevas} />
              <Dato etiqueta="Piezas que entran" valor={revision.resumen.piezas} />
              <Dato etiqueta="Sin costo cargado" valor={revision.resumen.sinCosto} />
            </dl>

            {revision.resumen.sinCosto > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {revision.resumen.sinCosto} producto(s) vienen sin costo. Se van a cargar igual,
                pero su margen no se va a poder calcular hasta que le pongas el costo.
              </div>
            )}

            {revision.resumen.productosQueYaEstaban > 0 && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                {revision.resumen.productosQueYaEstaban} producto(s) ya existen: se les actualiza
                precio y costo, y la cantidad de este archivo <strong>se suma</strong> a la
                existencia que ya tenían. El archivo dice «me llegaron», no «tengo».
              </div>
            )}

            {revision.malas.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="flex items-center gap-2 text-sm font-medium text-red-900">
                  <AlertTriangle className="h-4 w-4" />
                  {revision.malas.length} fila(s) no se van a cargar
                </p>
                <ul className="mt-2 space-y-1 text-xs text-red-800">
                  {revision.malas.slice(0, 25).map((m, i) => (
                    <li key={i}>
                      <span className="font-mono">línea {m.linea}</span> — {m.motivo}{' '}
                      {m.contenido && <span className="opacity-70">({m.contenido})</span>}
                    </li>
                  ))}
                </ul>
                {revision.malas.length > 25 && (
                  <p className="mt-1 text-xs text-red-700">
                    …y {revision.malas.length - 25} más.
                  </p>
                )}
                <p className="mt-2 text-xs text-red-800">
                  El resto sí se va a cargar. Puedes confirmar igual y corregir esas después.
                </p>
              </div>
            )}

            {revision.productos.length > 0 && (
              <div className="max-h-72 overflow-y-auto rounded-lg border border-surface-border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Código</th>
                      <th className="px-3 py-2">Producto</th>
                      <th className="px-3 py-2">Precio</th>
                      <th className="px-3 py-2">Costo</th>
                      <th className="px-3 py-2">Tallas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border">
                    {revision.productos.map((p) => (
                      <tr key={p.codigo}>
                        <td className="px-3 py-2 font-mono text-xs">{p.codigo}</td>
                        <td className="px-3 py-2">
                          {p.nombre}{' '}
                          {p.yaExiste && (
                            <Badge variant="info" className="ml-1">
                              ya existe
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums">{dinero(p.precio)}</td>
                        <td className="px-3 py-2 tabular-nums">
                          {p.costo == null ? (
                            <span className="text-amber-700">sin costo</span>
                          ) : (
                            dinero(p.costo)
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {p.variantes.map((v) => `${v.talla}${v.lugar ? ` · ${v.lugar}` : ''} (${v.cantidad})`).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                ¿De dónde viene esta mercancía?
              </label>
              <Input
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Tanda de la China del 20 de septiembre"
              />
              <p className="mt-1 text-xs text-slate-500">
                Queda en el libro de movimientos. Dentro de tres meses vas a querer saber de qué
                tanda era cada pieza.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => importar.mutate()}
                disabled={revision.productos.length === 0 || importar.isPending}
              >
                {importar.isPending
                  ? 'Cargando…'
                  : `Confirmar y cargar ${revision.productos.length} producto(s)`}
              </Button>
              <Button variant="ghost" onClick={() => setRevision(null)}>
                Volver a elegir
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: number }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{etiqueta}</dt>
      <dd className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">{valor}</dd>
    </div>
  );
}

async function descargarPlantilla() {
  const { csv } = await tienda.plantillaImportacion();
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla-inventario.csv';
  a.click();
  URL.revokeObjectURL(url);
}
